
import Vue from 'vue';
import { CvTabs, CvTab } from '@carbon/vue';
import { ShowAIPreviewOption } from '@/constants/imageProcessingEngine';
import { PRINTING_GROUPS } from '@/constants/printTechnique';
import { EMBROIDERY_CONVERTER_ACTIVE } from '@/constants/featureFlags';
import Close16 from '@carbon/icons-vue/es/close/16';
import Download16 from '@carbon/icons-vue/es/download/16';
import AiPreviewTooltip from './AiPreviewTooltip.vue';
import { PREVIEW_PRODUCT_BOX } from '../../../constants';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import { mapState, mapActions } from 'pinia';
import { ProductVariantImage } from '@midocean/webapps-shared-domain';
import { getScaledProductImageDPI } from '@/utils/areaHelper';
import { mm2px } from '@/utils';
import ArrowDown16 from '@carbon/icons-vue/es/arrow--down/16';
import ArrowUp16 from '@carbon/icons-vue/es/arrow--up/16';
import { AnnotationShape } from '@/web-components/import-wizard-v2/typings/common';
import AiPreviewSlideMenu from './AiPreviewSlideMenu.vue';

export default Vue.extend({
  name: 'ProductPreviewModal',

  components: {
    Close16,
    Download16,
    ArrowDown16,
    ArrowUp16,
    CvTabs,
    CvTab,
    AiPreviewTooltip,
    AiPreviewSlideMenu,
  },

  props: {
    isOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
    preSelectedPositionId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      PREVIEW_PRODUCT_BOX,
      positionId: this.preSelectedPositionId,
      displayImgsFrom: 0,
      product3dpreviewUrlsByPosition: {} as Record<string, string>,
      isProductPreviewUrlLoading: true,
      ShowAIPreviewOption,
      selectedTabIndex: 0,
      aiMenuExpanded: false,
      loadingPositions: new Set<string>(),
      shapeUrlCache: {} as Record<string, string>,
      aiPreviewUrlsByPosition: {} as Record<string, string>,
      isClosing: false,
    };
  },

  computed: {
    ...mapState(useProductStore, [
      'selectedVariant',
      'unavailablePrintPositions',
      'getProductPrintPosition',
      'getProductName',
      'getProductSku',
      'getAIGeneratedPhotographyAvailable',
    ]),
    ...mapState(useShapeStore, ['positionShapes']),
    selectedPosition(): ProductVariantImage | undefined {
      return this.selectedVariant.images.find(
        (x) => x.printPositionID === this.positionId
      );
    },

    currentPosition3dPreviewUrl(): string {
      return this.product3dpreviewUrlsByPosition[this.positionId] || '';
    },

    currentAiPreviewUrl(): string {
      return this.aiPreviewUrlsByPosition[this.positionId] || '';
    },

    show2DPreviewUrl(): boolean {
      return (
        !this.isProductPreviewUrlLoading &&
        this.currentPosition3dPreviewUrl !== ''
      );
    },

    showAIPreviewUrl(): boolean {
      return (
        !this.isProductPreviewUrlLoading && this.currentAiPreviewUrl !== ''
      );
    },

    selectedVariantPositions(): {
      imageUrl: string;
      positionName: string;
      id: string;
    }[] {
      const productVariantImages = this.selectedVariant.images.filter((x) =>
        this.unavailablePrintPositions.some(
          (p) => p.positionId === x.printPositionID
        )
      );

      return productVariantImages.map((x) => {
        const position = this.unavailablePrintPositions.find(
          (p) => p.positionId === x.printPositionID
        );

        return {
          id: x.printPositionID,
          imageUrl: x.imageThumbnailUrl,
          positionName: position?.positionName || '',
        };
      });
    },

    activePositionShapes(): {
      id: string;
      x: number;
      y: number;
      width: number;
      height: number;
      pngFileUrl: string;
      rotation: number;
    }[] {
      if (this.positionId) {
        const printPosition = this.getProductPrintPosition(this.positionId);

        if (printPosition) {
          const mirrorShapesFromPosition = this.getActiveMirrorShapes(
            this.positionId
          );
          const mirrorShapesFromPositionInArea = [] as AnnotationShape[];

          let selectedShapes = this.positionShapes(this.positionId).filter(
            (x) => x.isInArea
          ) as AnnotationShape[];

          mirrorShapesFromPosition.forEach((x) => {
            if (
              selectedShapes.findIndex((y) => y.id === x.originalShapeId) !== -1
            ) {
              mirrorShapesFromPositionInArea.push(x);
            }
          });

          selectedShapes = [
            ...selectedShapes,
            ...mirrorShapesFromPositionInArea,
          ];

          const mappedPositions = selectedShapes.map((x) => {
            const scaledDPI = getScaledProductImageDPI(
              printPosition.originalImageWidthPx,
              printPosition.originalImageHeightPx,
              printPosition.originalImageDpi,
              PREVIEW_PRODUCT_BOX
            );

            var variantImage = x.variantArtworkImages?.find(
              (v) => v.variantColor === this.selectedVariant.colour
            );

            const printArea = this.printPositionArea(printPosition.id, false);
            const isLanyard =
              printArea && printArea.kind === 'Rectangle'
                ? printArea.isLanyard
                : false;

            if (
              variantImage &&
              variantImage.widthPercentage > 0 &&
              variantImage.heightPercentage > 0 &&
              !isLanyard
            ) {
              let imageWidth = 0;
              let imageHeight = 0;
              if (
                printPosition.originalImageWidthPx >
                printPosition.originalImageHeightPx
              ) {
                imageWidth = PREVIEW_PRODUCT_BOX;
                imageHeight =
                  (imageWidth * printPosition.originalImageHeightPx) /
                  printPosition.originalImageWidthPx;
              } else {
                imageHeight = PREVIEW_PRODUCT_BOX;
                imageWidth =
                  (imageHeight * printPosition.originalImageWidthPx) /
                  printPosition.originalImageHeightPx;
              }

              // Use cached blob URL or create and cache new one
              let pngFileUrl = '';
              if (x.pngFile) {
                if (!this.shapeUrlCache[x.id]) {
                  this.shapeUrlCache[x.id] = URL.createObjectURL(
                    x.pngFile as Blob
                  );
                }
                pngFileUrl = this.shapeUrlCache[x.id];
              }

              return {
                id: x.id,
                x: variantImage.leftPercentage * imageWidth,
                y: variantImage.topPercentage * imageHeight,
                width: variantImage.widthPercentage * imageWidth,
                height: variantImage.heightPercentage * imageHeight,
                pngFileUrl,
                rotation: x.rotation,
              };
            }

            // Use cached blob URL or create and cache new one
            let pngFileUrl = '';
            if (x.pngFile) {
              if (!this.shapeUrlCache[x.id]) {
                this.shapeUrlCache[x.id] = URL.createObjectURL(
                  x.pngFile as Blob
                );
              }
              pngFileUrl = this.shapeUrlCache[x.id];
            }

            return {
              id: x.id,
              x: mm2px(x.xOnProductMM, scaledDPI),
              y: mm2px(x.yOnProductMM, scaledDPI),
              width: mm2px(x.widthOnProductMM, scaledDPI),
              height: mm2px(x.heightOnProductMM, scaledDPI),
              pngFileUrl,
              rotation: x.rotation,
            };
          });

          return mappedPositions;
        }
      }

      return [];
    },

    disclaimerMessage(): string {
      const position = this.unavailablePrintPositions.find(
        (p) => p.positionId === this.positionId
      );

      if (position && position.technique) {
        const groupCode = position.technique.groupCode;
        if (
          PRINTING_GROUPS.EMBROIDERY_GROUPS.includes(groupCode) &&
          EMBROIDERY_CONVERTER_ACTIVE
        ) {
          return this.$t('ProofComment38').toString();
        }
      }

      return '';
    },
  },

  watch: {
    isOpen(newValue) {
      if (newValue) {
        this.isClosing = false;
        this.$modal.show('product-preview-modal');
      } else {
        this.isClosing = true;
        this.$modal.hide('product-preview-modal');
      }
    },
  },

  beforeDestroy() {
    this.isClosing = true;
    this.clearCache();
  },

  async mounted() {
    if (this.isOpen) {
      this.$modal.show('product-preview-modal');
    }

    // Prevent API calls if component is closing
    if (this.isClosing) return;

    // Check cache before generating and prevent duplicate requests
    if (
      !Object.prototype.hasOwnProperty.call(
        this.product3dpreviewUrlsByPosition,
        this.positionId
      ) &&
      !this.loadingPositions.has(this.positionId)
    ) {
      this.loadingPositions.add(this.positionId);
      this.setIsLoaderActive(true);
      const generatedUrl = await this.generateProduct3dpreviewUrl(
        this.positionId
      );
      this.$set(
        this.product3dpreviewUrlsByPosition,
        this.positionId,
        generatedUrl
      );
      this.setIsLoaderActive(false);
      this.loadingPositions.delete(this.positionId);
    }
    this.isProductPreviewUrlLoading = false;
  },
  methods: {
    clearCache() {
      // Clean up all blob URLs to prevent memory leaks
      Object.values(this.shapeUrlCache).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      this.shapeUrlCache = {};

      // Clean up AI preview URLs
      Object.values(this.aiPreviewUrlsByPosition).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      this.aiPreviewUrlsByPosition = {};

      // Clean up 3D preview URLs
      Object.values(this.product3dpreviewUrlsByPosition).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      this.product3dpreviewUrlsByPosition = {};

      // Clear loading positions
      this.loadingPositions.clear();
    },
    ...mapActions(useMirrorStore, ['getActiveMirrorShapes']),
    ...mapActions(useProductStore, ['printPositionArea']),
    ...mapActions(useGlobalStore, ['setIsLoaderActive']),
    ...mapActions(useShapeStore, [
      'generateProduct3dpreviewUrl',
      'generateProductAIpreviewUrl',
    ]),
    scrollImageNavigation(start: number) {
      this.displayImgsFrom = start;
    },

    handleTryItClick() {
      // Activate AI preview tab (index 1)
      this.selectedTabIndex = 1;
      this.aiMenuExpanded = true;
    },

    async handleGenerateAIPreview() {
      this.selectedTabIndex = 1;
      this.aiMenuExpanded = false;
      // Revoke old blob URL before generating new one
      if (this.aiPreviewUrlsByPosition[this.positionId]) {
        URL.revokeObjectURL(this.aiPreviewUrlsByPosition[this.positionId]);
      }
      // Always regenerate AI preview on each click
      this.isProductPreviewUrlLoading = true;
      this.setIsLoaderActive(true);
      const generatedUrl = await this.generateProductAIpreviewUrl(
        this.positionId
      );
      this.$set(this.aiPreviewUrlsByPosition, this.positionId, generatedUrl);
      this.setIsLoaderActive(false);
      this.isProductPreviewUrlLoading = false;
    },

    async handleDownloadAIPreview() {
      try {
        const productName = this.getProductName || 'product';
        const positionName =
          this.getProductPrintPosition(this.positionId)?.name || 'position';
        // Sanitize filename by replacing special characters
        const sanitizedProductName = productName.replace(/[/\\?%*:|"<>]/g, '-');
        const sanitizedPositionName = positionName.replace(
          /[/\\?%*:|"<>]/g,
          '-'
        );
        const filename = `${sanitizedProductName}_${sanitizedPositionName}_ai-preview.png`;

        const imageUrl = this.currentAiPreviewUrl;

        // Fetch the blob and trigger download
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error('Failed to download AI preview:', error);
      }
    },

    async handleTabSelected(selectedIndex: number) {
      // Prevent API calls if component is closing
      if (this.isClosing) return;

      this.selectedTabIndex = selectedIndex;
      if (selectedIndex === 1) {
        this.aiMenuExpanded = true;
      }

      if (
        selectedIndex === 0 &&
        !Object.prototype.hasOwnProperty.call(
          this.product3dpreviewUrlsByPosition,
          this.positionId
        ) &&
        !this.loadingPositions.has(this.positionId)
      ) {
        this.loadingPositions.add(this.positionId);
        this.isProductPreviewUrlLoading = true;
        this.setIsLoaderActive(true);
        const generatedUrl = await this.generateProduct3dpreviewUrl(
          this.positionId
        );
        this.$set(
          this.product3dpreviewUrlsByPosition,
          this.positionId,
          generatedUrl
        );
        this.setIsLoaderActive(false);
        this.isProductPreviewUrlLoading = false;
        this.loadingPositions.delete(this.positionId);
      }
    },

    async handlePositionClick(positionId: string) {
      // Prevent API calls if component is closing
      if (this.isClosing) return;

      this.positionId = positionId;

      // Switch to Product preview tab when position changes
      this.selectedTabIndex = 0;

      // Check cache before generating and prevent duplicate requests
      if (
        !Object.prototype.hasOwnProperty.call(
          this.product3dpreviewUrlsByPosition,
          positionId
        ) &&
        !this.loadingPositions.has(positionId)
      ) {
        this.loadingPositions.add(positionId);
        this.setIsLoaderActive(true);
        const generatedUrl = await this.generateProduct3dpreviewUrl(positionId);
        this.$set(
          this.product3dpreviewUrlsByPosition,
          positionId,
          generatedUrl
        );
        this.setIsLoaderActive(false);
        this.loadingPositions.delete(positionId);
      }

      this.$emit('position-selected', positionId);
    },
    isImageHidden(index: number) {
      let start = this.displayImgsFrom;
      let end = this.displayImgsFrom + 3;
      let imagesToDisplay = Array.from(
        { length: end - start + 1 },
        (_, i) => start + i
      );

      return !imagesToDisplay.includes(index);
    },
  },
});
